'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Home, RefreshCw, ServerCrash, AlertTriangle } from 'lucide-react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('[App Error]', error);
  }, [error]);

  const router = useRouter();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-red-50/30 to-slate-100 p-6">
      {/* Subtle decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-red-100/40 blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-orange-100/40 blur-3xl translate-y-1/2 -translate-x-1/3" />
      </div>

      <div className="relative z-10 w-full max-w-lg text-center">

        {/* Illustration */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            {/* Outer ring */}
            <div className="w-32 h-32 rounded-full bg-red-100 border-2 border-red-200 flex items-center justify-center shadow-xl shadow-red-100">
              <ServerCrash size={52} className="text-red-400" strokeWidth={1.25} />
            </div>
            {/* Alert badge */}
            <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-amber-400 border-4 border-white flex items-center justify-center shadow-lg">
              <AlertTriangle size={16} className="text-white" strokeWidth={2.5} />
            </div>
            {/* Decorative dots */}
            <div className="absolute -bottom-3 -left-3 w-5 h-5 rounded-full bg-red-300/60" />
            <div className="absolute top-4 -right-6 w-3 h-3 rounded-full bg-amber-300/80" />
            <div className="absolute -top-5 left-6 w-4 h-4 rounded-full bg-red-200/70" />
          </div>
        </div>

        {/* Text */}
        <h1 className="text-3xl font-bold text-slate-800 mb-3">
          Oops! Something went wrong.
        </h1>
        <p className="text-slate-500 text-base leading-relaxed mb-2">
          We sincerely apologise for the inconvenience. An unexpected error occurred while processing your request.
        </p>
        <p className="text-slate-400 text-sm mb-8">
          Our team has been notified. You can try refreshing the page or head back to safety.
        </p>

        {/* Error detail (dev only) */}
        {error?.message && (
          <div className="mb-8 px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-left">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Error detail</p>
            <p className="text-xs font-mono text-slate-600 break-all">{error.message}</p>
            {error.digest && (
              <p className="text-xs text-slate-400 mt-1">ID: {error.digest}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => router.push('/')}
            className="flex items-center gap-2.5 h-11 px-6 text-sm font-semibold text-white rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-indigo-200"
            style={{ background: 'linear-gradient(135deg,#4f46e5,#6d28d9)' }}
          >
            <Home size={16} />
            Go to Home
          </button>
          <button
            onClick={() => reset()}
            className="flex items-center gap-2.5 h-11 px-6 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all active:scale-[0.98] shadow-sm"
          >
            <RefreshCw size={15} />
            Try Again
          </button>
        </div>

        {/* Footer */}
        <div className="mt-12 flex items-center justify-center gap-2 opacity-40">
          <div className="w-5 h-5 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <span className="text-white font-bold text-[10px]">A</span>
          </div>
          <span className="text-xs font-semibold text-slate-400 tracking-tight">APISTRATOR</span>
        </div>
      </div>
    </div>
  );
}
